// Derive capability chips and work-oriented jobs for the agent new-chat page.
// Framework-free so catalog, chat, and tests can share the same vocabulary.

const TYPE_META = {
  text: { label: 'Text', icon: 'align-left' },
  image: { label: 'Images', icon: 'image' },
  audio: { label: 'Audio', icon: 'music' },
  video: { label: 'Video', icon: 'video' },
  file: { label: 'Files', icon: 'file' },
};

const IMAGE_JOBS = [
  {
    id: 'generate',
    icon: 'image',
    label: 'Generate an image',
    description: 'Describe the subject, style, lighting, and composition',
    prompt: 'Generate an image. Describe the subject, setting, style, lighting, and any constraints I should follow.',
  },
  {
    id: 'iterate',
    icon: 'wand-magic-sparkles',
    label: 'Iterate on a look',
    description: 'Refine a direction until the visual matches',
    prompt: 'Help me iterate on a visual direction. I will describe what is close and what still feels off — propose concrete changes and generate the next version.',
  },
  {
    id: 'variations',
    icon: 'copy',
    label: 'Explore variations',
    description: 'Try alternate compositions, palettes, and moods',
    prompt: 'Create a few distinct variations of this idea. Change composition, palette, and mood so I can pick a direction.',
  },
];

const TEXT_JOBS = [
  {
    id: 'brief',
    icon: 'file-lines',
    label: 'Write a brief',
    description: 'Research the topic and ship a shareable document',
    prompt: 'Research this topic and write a polished, sourced brief I can share. Separate findings from recommendations and flag weak evidence.',
  },
  {
    id: 'review',
    icon: 'magnifying-glass',
    label: 'Review this',
    description: 'Find risks, gaps, and a clear recommendation',
    prompt: 'Review the attached material. Call out the strongest points, gaps, risks, and a recommended next step.',
  },
  {
    id: 'draft',
    icon: 'pen-to-square',
    label: 'Draft something',
    description: 'Turn a goal into a finished piece of writing',
    prompt: 'Draft this for me. Keep my meaning, tighten the structure, and make it ready to send.',
  },
  {
    id: 'research',
    icon: 'globe',
    label: 'Research this',
    description: 'Investigate a question and show the evidence',
    prompt: 'Research the latest developments on this topic. Summarize the strongest sources, note disagreements, and cite what you used.',
  },
];

function uniqueTypes(types) {
  return [...new Set((Array.isArray(types) ? types : []).map((kind) => String(kind || '').trim()).filter(Boolean))];
}

export function capabilitiesFromAgent(agent) {
  const toChips = (types) => uniqueTypes(types).map((kind) => ({
    kind,
    label: TYPE_META[kind]?.label || kind,
    icon: TYPE_META[kind]?.icon || 'circle',
  }));

  return {
    inputs: toChips(agent?.inputTypes),
    outputs: toChips(agent?.outputTypes),
  };
}

export function labelFromPrompt(prompt) {
  const text = String(prompt || '').replace(/\s+/g, ' ').trim();
  if (!text) return 'Get started';
  const firstSentence = text.split(/[.!?\n]/)[0].trim() || text;
  const words = firstSentence.split(/\s+/).filter(Boolean);
  const clipped = words.length <= 6 ? firstSentence : `${words.slice(0, 6).join(' ')}…`;
  return clipped.replace(/[:;,]+$/g, '').trim() || 'Get started';
}

export function descriptionFromPrompt(prompt) {
  const text = String(prompt || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= 90) return text;
  return `${text.slice(0, 88).trim()}…`;
}

function iconForAgent(agent) {
  const outputs = uniqueTypes(agent?.outputTypes);
  if (outputs.includes('image')) return 'image';
  if (outputs.includes('video')) return 'video';
  if (outputs.includes('audio')) return 'music';
  return 'briefcase';
}

export function agentIntroPrompt(agent) {
  const name = String(agent?.name || 'this agent').trim() || 'this agent';
  const description = String(agent?.description || '').replace(/\s+/g, ' ').trim();
  const parts = [`Introduce yourself as ${name}.`];
  if (description) {
    parts.push(`You specialize in this: ${description}`);
  }
  parts.push(
    'Explain what you can do for me and what you specialize in.',
    'Keep it concrete and brief, then ask what I want to work on first.',
  );
  return parts.join(' ');
}

export function jobsFromAgent(agent, { limit = 6 } = {}) {
  const prompts = (Array.isArray(agent?.defaultPrompts) ? agent.defaultPrompts : [])
    .map((prompt) => String(prompt || '').trim())
    .filter(Boolean);

  if (prompts.length) {
    const icon = iconForAgent(agent);
    return prompts.slice(0, limit).map((prompt, index) => ({
      id: `prompt-${index}`,
      icon,
      label: labelFromPrompt(prompt),
      description: descriptionFromPrompt(prompt),
      prompt,
    }));
  }

  const outputs = uniqueTypes(agent?.outputTypes);
  return (outputs.includes('image') ? IMAGE_JOBS : TEXT_JOBS).slice(0, limit);
}
